import { createContext, useState, useEffect, useContext } from 'react';
import api from './axiosInstance';

const AuthContext = createContext (null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(true);

    const logout = () => {
        localStorage.clear();
        setUser(null);
        setToken(null);
    }

    const fetchUser = async (tok = token) => {
        if (!tok) return null;
        try {
            const response = await api.get(`/invoco/users/me`, {
                headers: {
                    Authorization: `Bearer ${tok}`
                }
            });
            setUser(response.data);
            localStorage.setItem('currentUser', JSON.stringify(response.data));
            return response.data;
        } catch (error) {
            logout();
            return null;
        }
    }

    //Get Token and fetch User
    const login = async (username, password) => {
        try {
            const { data } = await api.post(
                `/auth/token`,
                new URLSearchParams({ grant_type: 'password', username, password }),
                { headers: 
                    {'Content-Type': 'application/x-www-form-urlencoded'}
                }
            );
            localStorage.setItem('token', data.access_token);
            setToken(data.access_token);

            return await fetchUser(data.access_token)
        } catch (error) {
            throw new Error('Invalid username or password');
        }
    };

    // User self-serve ONLY
    const requestPasswordReset = async (email) => {
        try {
            await api.post('/auth/forgot-password', { email });
        } catch (error) {
            throw new Error('Password reset failed');
        }
    };
    
    const resetPassword = async (token, password) => {
        try {
            const response = await api.post('/auth/reset-password', { token, password });
            return response.data;
        } catch (error) {
            throw new Error('Password reset failed - please contact your adminitrator');
        }
    };

    useEffect(() => {
        const reqInterceptor = api.interceptors.request.use((config) => {
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });

        const resInterceptor = api.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    logout();
                }
                return Promise.reject(error);
            }
        )
        return () => {
            api.interceptors.request.eject(reqInterceptor);
            api.interceptors.response.eject(resInterceptor);
        }
    }, [token]);

    useEffect(() => {
        const initAuth = async () => {
            if (token) {
                await fetchUser();
            }
            setLoading(false);
        };
        initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])



    return (
        <AuthContext.Provider value={{ user, token, login, logout, loading, requestPasswordReset, resetPassword }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext);